import { formatCurrency } from '@mahawi/eshop-common/src/format-currency';
import { round } from '@mahawi/eshop-common/src/round';
import { type ICurrencyType } from '@mahawi/eshop-common/src/types';
import { Space, Tag, Typography } from 'antd';
import React from 'react';

import { EMPTY_VALUE } from '../../fragments/empty-value';
import { SPACING } from '../../theme/tokens';
import CopyToClipboard from '../copy-to-clipboard';
import StatusIcon from '../status-icon';

const { Text } = Typography;

interface IPriceDisplayProps {
  orderPriceWithTax?: number;
  priceWithoutTax?: number;
  deliveryFee?: number;
  deliveryFeeWithoutTax?: number;
  packagesCount: number;
  companyHasPricesWithoutTax: boolean;
  currencyType?: ICurrencyType;
}

export const PriceDisplay = React.memo(function PriceDisplay({
  orderPriceWithTax,
  priceWithoutTax,
  deliveryFee,
  deliveryFeeWithoutTax,
  packagesCount,
  companyHasPricesWithoutTax,
  currencyType,
}: IPriceDisplayProps): React.ReactElement {
  const itemsPriceWithTax: number | undefined =
    orderPriceWithTax && deliveryFee
      ? round(orderPriceWithTax - deliveryFee, currencyType?.decimalPrecision)
      : undefined;

  const itemsPriceWithoutTax: number | undefined =
    priceWithoutTax && deliveryFeeWithoutTax
      ? round(
          priceWithoutTax - deliveryFeeWithoutTax,
          currencyType?.decimalPrecision,
        )
      : undefined;

  const totalTax: number | undefined =
    orderPriceWithTax && priceWithoutTax
      ? round(
          orderPriceWithTax - priceWithoutTax,
          currencyType?.decimalPrecision,
        )
      : undefined;

  return (
    <Space size={SPACING.none} orientation="vertical">
      {companyHasPricesWithoutTax && (
        <Tag key="companyHasPricesWithoutTax" color="orange">
          Company has prices without tax
        </Tag>
      )}

      <Space size={SPACING.xs} align="baseline">
        <Text>total price:</Text>
        {!companyHasPricesWithoutTax && <StatusIcon ok={true} />}
        <Text strong>
          {orderPriceWithTax
            ? formatCurrency(orderPriceWithTax, currencyType)
            : EMPTY_VALUE}
        </Text>
        <CopyToClipboard text={String(orderPriceWithTax)} type="link" />
      </Space>

      <Space size={SPACING.xs} align="baseline">
        <Text>total price without tax:</Text>
        {companyHasPricesWithoutTax && <StatusIcon ok={true} />}
        <Text strong>
          {priceWithoutTax
            ? formatCurrency(priceWithoutTax, currencyType)
            : EMPTY_VALUE}
        </Text>
        <CopyToClipboard text={String(priceWithoutTax)} type="link" />
      </Space>

      <Space size={SPACING.xs} align="baseline">
        <Text>total tax:</Text>
        <Text strong>
          {totalTax ? formatCurrency(totalTax, currencyType) : EMPTY_VALUE}
        </Text>
        <CopyToClipboard text={String(totalTax)} type="link" />
      </Space>

      <Space size={SPACING.xs} align="baseline">
        <Text>items price:</Text>
        {!companyHasPricesWithoutTax && <StatusIcon ok={true} />}
        <Text strong>
          {itemsPriceWithTax
            ? formatCurrency(itemsPriceWithTax, currencyType)
            : EMPTY_VALUE}
        </Text>
        {itemsPriceWithTax && (
          <CopyToClipboard text={String(itemsPriceWithTax)} type="link" />
        )}
      </Space>

      <Space size={SPACING.xs} align="baseline">
        <Text>items price without tax:</Text>
        {companyHasPricesWithoutTax && <StatusIcon ok={true} />}
        <Text strong>
          {itemsPriceWithoutTax
            ? formatCurrency(itemsPriceWithoutTax, currencyType)
            : EMPTY_VALUE}
        </Text>
        {itemsPriceWithoutTax && (
          <CopyToClipboard text={String(itemsPriceWithoutTax)} type="link" />
        )}
      </Space>

      <Space size={SPACING.xs} align="baseline">
        <Text>delivery fee:</Text>
        {!companyHasPricesWithoutTax && <StatusIcon ok={true} />}
        <Text strong>
          {deliveryFee
            ? formatCurrency(deliveryFee, currencyType)
            : EMPTY_VALUE}
        </Text>
        <CopyToClipboard text={String(deliveryFee)} type="link" />
      </Space>

      <Space size={SPACING.xs} align="baseline">
        <Text>delivery fee without tax:</Text>
        {companyHasPricesWithoutTax && <StatusIcon ok={true} />}
        <Text strong>
          {deliveryFeeWithoutTax
            ? formatCurrency(deliveryFeeWithoutTax, currencyType)
            : EMPTY_VALUE}
        </Text>
        <CopyToClipboard text={String(deliveryFeeWithoutTax)} type="link" />
      </Space>

      <Space size={SPACING.xs} align="baseline">
        <Text>packages count:</Text>
        <Text strong>{packagesCount}</Text>
      </Space>
    </Space>
  );
});
